/**
 * InvoiceFormModal
 * 세금계산서 추가/수정 모달
 */

import React, { useState, useEffect } from 'react';
import { TaxInvoice, CreateInvoiceRequest } from '../../models/Tax';
import { getTodayKST } from '../../../../utils/dateUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateInvoiceRequest) => Promise<boolean>;
  editItem?: TaxInvoice | null;
}

export const InvoiceFormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  editItem,
}) => {
  const [form, setForm] = useState({
    direction: 'purchase' as string,
    date: '',
    counterpart_name: '',
    business_number: '',
    supply_amount: '',
    tax_amount: '',
    total: '',
    memo: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editItem) {
      setForm({
        direction: editItem.direction,
        date: editItem.date,
        counterpart_name: editItem.counterpart_name,
        business_number: editItem.business_number || '',
        supply_amount: String(editItem.supply_amount),
        tax_amount: String(editItem.tax_amount),
        total: String(editItem.total),
        memo: editItem.memo || '',
      });
    } else {
      setForm({
        direction: 'purchase',
        date: getTodayKST(),
        counterpart_name: '',
        business_number: '',
        supply_amount: '',
        tax_amount: '',
        total: '',
        memo: '',
      });
    }
  }, [editItem, isOpen]);

  const handleAmountChange = (field: 'supply_amount' | 'tax_amount' | 'total', value: string) => {
    const numVal = value.replace(/[^0-9-]/g, '');
    const newForm = { ...form, [field]: numVal };

    if (field === 'supply_amount' && numVal) {
      const supplyVal = parseInt(numVal);
      const taxVal = Math.round(supplyVal * 0.1);
      newForm.tax_amount = String(taxVal);
      newForm.total = String(supplyVal + taxVal);
    } else if (field === 'total' && numVal) {
      const totalVal = parseInt(numVal);
      const taxVal = Math.round(totalVal / 11);
      newForm.tax_amount = String(taxVal);
      newForm.supply_amount = String(totalVal - taxVal);
    }

    setForm(newForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.date || !form.counterpart_name || !form.supply_amount) return;

    setSubmitting(true);
    const success = await onSubmit({
      direction: form.direction,
      date: form.date,
      counterpart_name: form.counterpart_name,
      business_number: form.business_number || undefined,
      supply_amount: parseInt(form.supply_amount) || 0,
      tax_amount: parseInt(form.tax_amount) || 0,
      total: parseInt(form.total) || 0,
      memo: form.memo || undefined,
    });
    setSubmitting(false);
    if (success) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {editItem ? '세금계산서 수정' : '세금계산서 추가'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* 구분 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">구분 *</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="direction"
                  value="purchase"
                  checked={form.direction === 'purchase'}
                  onChange={(e) => setForm({ ...form, direction: e.target.value })}
                  className="mr-2"
                />
                <span className="text-sm">매입 (받은 것)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="direction"
                  value="sales"
                  checked={form.direction === 'sales'}
                  onChange={(e) => setForm({ ...form, direction: e.target.value })}
                  className="mr-2"
                />
                <span className="text-sm">매출 (발행한 것)</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">날짜 *</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">거래처명 *</label>
            <input
              type="text"
              value={form.counterpart_name}
              onChange={(e) => setForm({ ...form, counterpart_name: e.target.value })}
              placeholder="예: (주)이루는자리"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">사업자번호</label>
            <input
              type="text"
              value={form.business_number}
              onChange={(e) => setForm({ ...form, business_number: e.target.value })}
              placeholder="123-45-67890"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">공급가액 *</label>
              <input
                type="text"
                value={form.supply_amount}
                onChange={(e) => handleAmountChange('supply_amount', e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">세액</label>
              <input
                type="text"
                value={form.tax_amount}
                onChange={(e) => handleAmountChange('tax_amount', e.target.value)}
                placeholder="자동 (10%)"
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">합계</label>
              <input
                type="text"
                value={form.total}
                onChange={(e) => handleAmountChange('total', e.target.value)}
                placeholder="자동계산"
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">공급가액 입력 시 세액(10%)과 합계가 자동 계산됩니다</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
            <textarea
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              취소
            </button>
            <button type="submit" disabled={submitting} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {submitting ? '저장 중...' : editItem ? '수정' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
